import React, { useContext, useEffect, useState } from 'react';
import GroupsIcon from '@mui/icons-material/Groups';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import { Trans, useTranslation } from 'react-i18next';
import { Avatar, Box, Checkbox, FormLabel, Tooltip, Typography } from '@mui/material';
import { getMediaLogo } from '../../utils/file';
import { IPageCard } from '../../interfaces/campaign';
import { formatMembersCount, formatPricePersian } from '../../utils/number';
import { CampaignContext } from '../../contexts/CampaignContext';
import { InfoRounded } from '@mui/icons-material';

const PageCard: React.FC<IPageCard> = ({ id, entity_id, title, price, image, media, category, handle_name, er, location, followers_count, price_description, price_title, onSelect, checkbox = true }) => {
	const { t } = useTranslation();
	const [isChecked, setIsChecked] = useState(false);

	const { selectedPages, viewSelectedItemsMode, categoryActiveButton } = useContext(CampaignContext);

	const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const checked = event.target.checked;
		setIsChecked(checked);
		if (onSelect) {
			const data = { id, entity_id, title, price, image, media, category, handle_name, er, location, followers_count, price_description, price_title };
			onSelect(data, checked);
		}
	};

	const _price = formatPricePersian(price);

	useEffect(() => {
		setIsChecked(selectedPages.some(pkg => pkg.id === id));
	}, [selectedPages, id]);

	return (
		<Box className="relative flex w-64 flex-col items-center justify-center rounded-2xl border border-grey-300 p-4">
			<Avatar src={getMediaLogo(media)} alt="platform img" className="absolute right-3 top-3 h-12 w-12" />
			<Avatar src={image} alt="page img" className="mb-4 h-28 w-28" />
			<Box className="flex w-full items-center justify-between border-b py-2">
				<Box className="flex items-center">
					{checkbox && <Checkbox size="large" className="me-2 p-0 text-grey-400" checked={isChecked} onChange={handleCheckboxChange} disabled={viewSelectedItemsMode} />}
					<FormLabel className="text-base font-medium text-text-primary">{title}</FormLabel>
				</Box>
				<Typography className="text-sm font-medium text-text-primary">{handle_name}</Typography>
			</Box>
			<Box className="flex w-full items-center justify-between border-b pb-2 pt-3">
				<Box className="flex items-center">
					<GroupsIcon className="me-2 h-5 w-5 text-grey-400" />
					<Typography className="text-sm text-text-secondary">
						{t('title')}
						<Tooltip title={price_description ?? ''} className="z-50">
							<InfoRounded color="primary" fontSize="small" />
						</Tooltip>
					</Typography>
				</Box>
				<Typography className="text-sm font-medium text-text-primary">{price_title ?? ''}</Typography>
			</Box>
			<Box className="flex w-full items-center justify-between border-b pb-2 pt-3">
				<Box className="flex items-center">
					<GroupsIcon className="me-2 h-5 w-5 text-grey-400" />
					<Typography className="text-sm text-text-secondary">{t('member_count')}</Typography>
				</Box>
				<Typography className="text-sm font-medium text-text-primary">{formatMembersCount(followers_count)}</Typography>
			</Box>
			{categoryActiveButton !== 0 && (
				<Box className="flex w-full items-center justify-between border-b pb-2 pt-3">
					<Box className="flex items-center">
						<LocationOnIcon className="me-2 h-5 w-5 text-grey-400" />
						<Typography className="text-sm text-text-secondary">{t('location')}</Typography>
					</Box>
					<Typography className="text-sm font-medium text-text-primary">{location?.title ?? '-'}</Typography>
				</Box>
			)}
			<Box className="flex w-full items-center justify-between border-b pb-2 pt-3">
				<Box className="flex items-center">
					<InsertChartIcon className="me-2 h-5 w-5 text-grey-400" />
					<Typography className="text-sm text-text-secondary">{t('er')}</Typography>
				</Box>
				<Typography className="text-sm font-medium text-text-primary">{er}</Typography>
			</Box>
			<Box className="flex w-full items-center justify-between border-b pb-2 pt-3">
				<Box className="flex items-center">
					<ContentPasteIcon className="me-2 h-5 w-5 text-grey-400" />
					<Typography className="text-sm text-text-secondary">{t('content')}</Typography>
				</Box>
				<Typography className="text-sm font-medium text-text-primary">{category}</Typography>
			</Box>
			<Box className="flex w-full items-center justify-between pb-2 pt-3">
				<Box className="flex items-center">
					<AccountBalanceWalletIcon className="me-2 h-5 w-5 text-grey-400" />
					<Typography className="text-sm text-text-secondary">{t('price')}</Typography>
				</Box>
				<Trans i18nKey="price_value_label" values={{ _price }} components={[<Typography className="text-sm font-medium text-text-primary" />]} />
			</Box>
		</Box>
	);
};

export default PageCard;
